import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../App';
import api, { getErrorMessage } from '../utils/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Clock, Bed, Users, ListChecks, Plus, PencilSimple, Robot } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ClientTrackingModal from '../components/ClientTrackingModal';
import EditBatchModal from '../components/EditBatchModal';
import AIGuideDrawer from '../components/AIGuideDrawer';
import { RequestEditControl } from '../components/EditRequests';

// Convert 24-hour time to 12-hour AM/PM format
const convertTo12Hour = (time24) => {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const BatchBedSchedule = () => {
  const { activeBranch } = useBranch();
  const { user } = useAuth();
  const [schedule, setSchedule] = useState([]);
  const [waitingList, setWaitingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showClientTracking, setShowClientTracking] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedBatchTime, setSelectedBatchTime] = useState(null);
  const [showEditBatch, setShowEditBatch] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  
  // AI Guide states
  const [showAIGuide, setShowAIGuide] = useState(false);
  const [selectedGuideCustomer, setSelectedGuideCustomer] = useState(null);
  const [editAccess, setEditAccess] = useState({});

  const fetchEditAccess = async () => {
    if (!activeBranch?.branch_id) return;
    try {
      const res = await api.get('/edit-requests/mine', { params: { branch_id: activeBranch.branch_id } });
      setEditAccess(res.data || {});
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchEditAccess(); const t = setInterval(fetchEditAccess, 15000); return () => clearInterval(t); }, [activeBranch]); // eslint-disable-line react-hooks/exhaustive-deps

  const requestEdit = async (batch, customerData) => {
    try {
      const res = await api.post('/edit-requests', {
        customer_id: customerData.customer_id,
        batch_id: batch.batch_id,
        batch_time: batch.start_time,
        batch_type: 'regular',
        branch_id: activeBranch?.branch_id,
      });
      toast.success(res.data.message);
      fetchEditAccess();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to send request'));
    }
  };

  const openTracking = (batch, customerData) => {
    setSelectedCustomer({ customer_id: customerData.customer_id, name: customerData.customer_name, reg_no: customerData.customer_reg_no });
    setSelectedBatchTime(batch.start_time);
    setShowClientTracking(true);
  };
  
  // Check if user is admin or owner
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner';

  const handleGenerateBatches = async (forceRegenerate = false) => {
    try {
      setGenerating(true);
      
      // Check if we have an active branch
      if (!activeBranch) {
        toast.error('No branch available. Initializing system...');
        
        // Call initialize-system to create default branch and batches
        const initResponse = await api.post('/admin/initialize-system');
        toast.success(initResponse.data.message || 'System initialized successfully');
        
        // Refresh branches so activeBranch gets set
        window.location.reload();
        return;
      }
      
      // Generate batches for the active branch
      const response = await api.post('/batches/generate-today', {
        branch_id: activeBranch.branch_id,
        force: forceRegenerate
      });
      
      const message = response.data.message || 'Batches generated successfully';
      
      // If batches already exist but schedule is empty, suggest force regenerate
      if (message.includes('already exist') && schedule.length === 0 && !forceRegenerate) {
        toast.warning(
          'Batches exist but not showing. Click again to force regenerate.',
          { duration: 5000 }
        );
        // Auto-trigger force regenerate after 2 seconds
        setTimeout(() => handleGenerateBatches(true), 2000);
      } else {
        toast.success(message);
        fetchSchedule(); // Refresh the schedule
      }
    } catch (error) {
      console.error('Generate batches error:', error);
      toast.error(getErrorMessage(error, 'Failed to generate batches'));
    } finally {
      setGenerating(false);
    }
  };

  const handleFixAllBatches = async () => {
    if (!window.confirm('This will delete all existing batches for today and recreate them for all branches. Continue?')) {
      return;
    }
    
    try {
      setGenerating(true);
      const response = await api.post('/admin/fix-batches');
      toast.success(response.data.message || 'All batches fixed successfully');
      
      // Wait a moment then refresh
      setTimeout(() => {
        fetchSchedule();
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Fix batches error:', error);
      toast.error(getErrorMessage(error, 'Failed to fix batches'));
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (activeBranch) {
      fetchSchedule();
      // Auto-refresh every 15 seconds
      const interval = setInterval(fetchSchedule, 15000);
      return () => clearInterval(interval);
    }
  }, [activeBranch]);

  const fetchSchedule = async () => {
    try {
      const [response, waitingResponse] = await Promise.all([
        api.get('/batches/schedule', { params: { branch_id: activeBranch?.branch_id } }),
        api.get('/attendance/today-checkins', { params: { branch_id: activeBranch?.branch_id } }),
      ]);
      setSchedule(response.data.batches || []);
      
      const waiting = (waitingResponse.data.checkins || []).filter(
        c => c.status === 'waiting'
      );
      setWaitingList(waiting);
      
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Loading schedule...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6" data-testid="batch-bed-schedule">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#2C4C3B]">Batch & Bed Schedule</h1>
            <p className="text-sm sm:text-base text-[#1C1C1A]/60">Complete schedule showing all batches and bed assignments</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            {isAdminOrOwner && (
              <Button
                onClick={() => handleGenerateBatches(false)}
                disabled={generating}
                className="bg-[#2C4C3B] hover:bg-[#1e3429] text-white text-sm"
              >
                <Plus size={18} className="mr-2" />
                {generating ? 'Generating...' : 'Generate 16 Batches'}
              </Button>
            )}
            {isAdminOrOwner && schedule.length === 0 && (
              <Button
                onClick={handleFixAllBatches}
                disabled={generating}
                variant="outline"
                className="border-red-500 text-red-500 hover:bg-red-50 text-sm"
              >
                🔧 Fix All Batches
              </Button>
            )}
            <div className="text-xs sm:text-sm text-[#1C1C1A]/60 text-center sm:text-left">
              Auto-refreshes every 15 seconds
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Card className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Clock size={20} className="text-[#2C4C3B] sm:w-6 sm:h-6" />
              <div>
                <p className="text-xs text-[#1C1C1A]/60">Total Batches</p>
                <p className="text-xl sm:text-2xl font-bold text-[#2C4C3B]">{schedule.length}</p>
              </div>
            </div>
          </Card>

          <Card className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Bed size={20} className="text-[#2C4C3B] sm:w-6 sm:h-6" />
              <div>
                <p className="text-xs text-[#1C1C1A]/60">Total Beds</p>
                <p className="text-xl sm:text-2xl font-bold text-[#2C4C3B]">
                  {schedule.reduce((sum, b) => sum + (b.capacity || 15), 0)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Users size={20} className="text-green-600 sm:w-6 sm:h-6" />
              <div>
                <p className="text-xs text-[#1C1C1A]/60">Occupied Beds</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600">
                  {schedule.reduce((sum, b) => sum + b.occupied, 0)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <ListChecks size={20} className="text-orange-600 sm:w-6 sm:h-6" />
              <div>
                <p className="text-xs text-[#1C1C1A]/60">Waiting List</p>
                <p className="text-xl sm:text-2xl font-bold text-orange-600">
                  {waitingList.length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Waiting List Section */}
        {waitingList.length > 0 && (
          <Card className="p-4 sm:p-6 bg-orange-50 border-orange-200">
            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              <ListChecks size={20} className="text-orange-600 sm:w-6 sm:h-6" weight="fill" />
              <h2 className="text-lg sm:text-xl font-semibold text-[#2C4C3B]">
                Waiting List ({waitingList.length} customers)
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {waitingList.map((customer, idx) => (
                <div key={idx} className="bg-white p-3 rounded-lg border border-orange-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-[#2C4C3B] text-sm sm:text-base">{customer.customer_name}</p>
                      <p className="text-xs sm:text-sm text-[#1C1C1A]/60">Token #{customer.token_number}</p>
                    </div>
                    <Badge className="bg-orange-500 text-white text-xs">Waiting</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Batch Schedule with Bed Layout */}
        <div className="space-y-4 sm:space-y-6">
          {schedule.map((batch, batchIdx) => (
            <Card key={batch.batch_id} className="p-4 sm:p-6">
              {/* Batch Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 pb-4 border-b border-[#E5E5DF] gap-3">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center ${
                    batch.is_current ? 'bg-green-100' : 'bg-[#E2E8E4]'
                  }`}>
                    <span className="text-lg sm:text-xl font-bold text-[#2C4C3B]">
                      {batchIdx + 1}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                      <h3 className="text-xl sm:text-2xl font-bold text-[#2C4C3B]">
                        {convertTo12Hour(batch.start_time)} - {convertTo12Hour(batch.end_time)}
                      </h3>
                      <div className="flex gap-2">
                        {batch.is_current && (
                          <Badge className="bg-green-600 text-white text-xs">Active Now</Badge>
                        )}
                        {batch.alert_level === 'full' && (
                          <Badge variant="destructive" className="text-xs">FULL</Badge>
                        )}
                        {batch.alert_level === 'warning' && (
                          <Badge className="bg-orange-500 text-white text-xs">High Capacity</Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-[#1C1C1A]/60 mt-1">
                      Batch {batchIdx + 1} of 16 • 40 minutes
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <div className="text-left sm:text-right">
                    <p className="text-2xl sm:text-3xl font-bold text-[#2C4C3B]">
                      {batch.occupied}/{batch.capacity || 15}
                    </p>
                    <p className="text-xs sm:text-sm text-[#1C1C1A]/60">Beds Occupied</p>
                  </div>
                  {isAdminOrOwner && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedBatch(batch);
                        setShowEditBatch(true);
                      }}
                      className="border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white"
                    >
                      <PencilSimple size={16} className="mr-1" />
                      Edit Batch
                    </Button>
                  )}
                </div>
              </div>

              {/* Bed Grid - Dynamic based on capacity */}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
                {[...Array(batch.capacity || 15)].map((_, bedIdx) => {
                  const bedNumber = bedIdx + 1;
                  const bedData = batch.bed_layout[bedNumber];
                  const isOccupied = !!bedData;
                  const isCompleted = bedData?.completed || false;
                  
                  // Find the full customer data from the batch.customers array
                  // Completed customers are not in batch.customers, so fall back to the bed_layout entry
                  const customerData = batch.customers?.find(c => c.bed_number === bedNumber)
                    || (bedData?.customer_id ? { customer_id: bedData.customer_id, customer_name: bedData.customer_name, customer_reg_no: bedData.customer_reg_no } : null);

                  return (
                    <div
                      key={bedNumber}
                      onClick={() => {
                        if (isOccupied && customerData && (!isCompleted || editAccess[customerData.customer_id] === 'approved')) {
                          openTracking(batch, customerData);
                        }
                      }}
                      className={`relative p-2 sm:p-3 md:p-4 rounded-lg border-2 transition-all ${
                        isCompleted
                          ? `bg-gray-100 border-gray-300 opacity-70 ${customerData && editAccess[customerData.customer_id] === 'approved' ? 'cursor-pointer ring-2 ring-[#2C4C3B]/40 opacity-100' : ''}`
                          : isOccupied
                          ? 'bg-red-50 border-red-300 cursor-pointer hover:shadow-lg hover:scale-105'
                          : 'bg-green-50 border-green-300'
                      }`}
                      title={isOccupied && !isCompleted ? `Click to view ${bedData.customer_name}'s tracking` : ''}
                    >
                      {/* Bed Number */}
                      <div className="flex items-center justify-between mb-1 sm:mb-2">
                        <Bed
                          size={16}
                          className={`${
                            isCompleted 
                              ? 'text-gray-500' 
                              : isOccupied 
                              ? 'text-red-600' 
                              : 'text-green-600'
                          } sm:w-5 sm:h-5`}
                          weight="fill"
                        />
                        <span className={`text-xs font-bold ${
                          isCompleted
                            ? 'text-gray-700'
                            : isOccupied 
                            ? 'text-red-900' 
                            : 'text-green-900'
                        }`}>
                          #{bedNumber}
                        </span>
                      </div>

                      {/* Customer Info or Available Status */}
                      {isOccupied ? (
                        <div className="space-y-0.5 sm:space-y-1">
                          <p className={`text-xs sm:text-sm font-semibold truncate ${
                            isCompleted ? 'text-gray-700' : 'text-red-900'
                          }`}>
                            {bedData.customer_name}
                          </p>
                          {bedData.token_number && (
                            <p className={`text-[10px] sm:text-xs ${
                              isCompleted ? 'text-gray-600' : 'text-red-700'
                            }`}>
                              Token #{bedData.token_number}
                            </p>
                          )}
                          {!isCompleted && (
                            <p className="text-[9px] sm:text-[10px] text-red-600 font-medium mt-1">
                              Click to track
                            </p>
                          )}
                          {bedData.customer_reg_no && (
                            <p className={`text-xs ${
                              isCompleted ? 'text-gray-600' : 'text-red-600'
                            }`}>
                              {bedData.customer_reg_no}
                            </p>
                          )}
                          {isCompleted && (
                            <Badge className="bg-gray-500 text-white text-[10px] mt-1">
                              Done
                            </Badge>
                          )}
                          {isCompleted && customerData && (
                            <RequestEditControl
                              status={editAccess[customerData.customer_id]}
                              onRequest={() => requestEdit(batch, customerData)}
                              onOpen={() => openTracking(batch, customerData)}
                            />
                          )}
                          {!isCompleted && (
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedGuideCustomer({ id: customerData.customer_id, name: customerData.customer_name });
                                setShowAIGuide(true);
                              }}
                              className="w-full mt-2 bg-teal-500 hover:bg-teal-600 text-white text-xs py-1"
                            >
                              <Robot size={12} weight="fill" className="mr-1" />
                              Guide
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="text-center">
                          <p className="text-sm font-semibold text-green-700">
                            Available
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Client Tracking Modal */}
      <ClientTrackingModal
        open={showClientTracking}
        onClose={() => {
          setShowClientTracking(false);
          setSelectedCustomer(null);
          setSelectedBatchTime(null);
          fetchEditAccess();
        }}
        customer={selectedCustomer}
        batchTime={selectedBatchTime ? convertTo12Hour(selectedBatchTime) : null}
      />

      {/* Edit Batch Modal */}
      <EditBatchModal
        open={showEditBatch}
        onClose={() => {
          setShowEditBatch(false);
          setSelectedBatch(null);
        }}
        batch={selectedBatch}
        onSuccess={() => {
          fetchSchedule();
        }}
      />

      {/* AI Guide Drawer */}
      <AIGuideDrawer
        isOpen={showAIGuide}
        onClose={() => {
          setShowAIGuide(false);
          setSelectedGuideCustomer(null);
        }}
        customerId={selectedGuideCustomer?.id}
        customerName={selectedGuideCustomer?.name}
      />
    </Layout>
  );
};

export default BatchBedSchedule;
